import { i18n } from '@lingui/core';
import { Trans, t } from '@lingui/macro';
import React, { useState } from 'react';
import * as yup from 'yup';
import { useQuery } from '../../common/hooks/useQuery';
import { useYupValidationForm } from '../../common/hooks/useYupValidationForm';
import { DEFAULT_REDIRECT_URI } from '../../constants';
import {changePass, getBuildInfo, resetPassword} from '../../util/APIUtils';
import { error, success } from '../../util/toast';
import AuthLayout from '../layout';
import {generateDeviceId, getDeviceInfo} from "../../util";
import moment from "moment/moment";

const validationSchema = yup.object({
  newPassword: yup
    .string()
    .trim()
    .matches(
      /^(?=.*[A-Z])(?=.*[!@#$&*])(?=.*[0-9])(?=.*[a-z]).{8,}$/,
      `Tối thiểu 8 ký tự bao gồm số, chữ hoa, chữ thường và 1 ký tự đặc biệt`,
    ),
  retypePassword: yup
    .string()
    .oneOf([yup.ref('newPassword'), null], `Mật khẩu không khớp!`),
});

const ChangePassword = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useYupValidationForm(validationSchema);
  const [showPass, setShowPass] = useState({ newPassword: false, retypePassword: false });
  const query = useQuery();
  const key = query.get('key');
  const onSubmit = async (data) => {
    const {deviceId,appVersion,deviceName} = await getDeviceInfo(query);
    let reqData;
    if (key) {
      reqData = {
        key,
        newPassword: data.newPassword,
        deviceId,
        deviceName,
        appVersion,
      };
      resetPassword(reqData)
        .then((response) =>
          response.json().then((rs) => {
            const { id_token } = rs;
            if (!id_token) {
              throw new Error();
            }
            const redirectParam = query.get('redirect');
            const redirect = redirectParam ?? DEFAULT_REDIRECT_URI;
            const pathArr = redirect.split('/');
            const domain =
              pathArr.length > 3 ? `${pathArr[0]}//${pathArr[2]}` : redirect;
            const stringQuery = new URLSearchParams();
            stringQuery.append('token', id_token);
            stringQuery.append('url', redirect);
            window.location.href = `${domain}/redirect?${stringQuery.toString()}`;
          }),
        )
        .catch((err) => {
          console.log(err);
          error(
            (err ? err.detail : '') ||
            i18n._(t`Oops, có lỗi xảy ra, vui lòng thử lại sau`),
          );
        });
    } else {
      reqData = {
        newPassword: data.newPassword,
      };
      changePass(reqData)
        .then(() => {
          success(i18n._(t`Đổi mật khẩu thành công`));
          setTimeout(() => {
            window.location.href = DEFAULT_REDIRECT_URI;
          }, 3700);
        })
        .catch((err) => {
          error(
            (err && err.detail) ||
            i18n._(t`Oops, có lỗi xảy ra, vui lòng thử lại sau`),
          );
        });
    }
  };

  return (
    <AuthLayout>
      <div className={'bg-[#c4c4c47f]'}>
        <div className={'my-5 mx-auto md:my-5 lg:my-8 w-96 text-center'}>
          <h1 className={'text-luat_HN_primary font-bold text-3xl'}>
            <Trans>Đặt lại mật khẩu</Trans>
          </h1>
        </div>
        <form className={'flex flex-col'} onSubmit={handleSubmit(onSubmit)}>
          <div className={'mx-10 py-1 lg:py-3'}>
            <div className={`font-bold mr-2 mt-1 text-luat_HN_primary`}>
              <Trans>Mật khẩu mới</Trans>
            </div>
            <div className="relative w-full">
              <input
                type={showPass.newPassword ? 'text' : 'password'}
                className={`w-full py-2 pl-5 pr-10 rounded-xl focus:ring outline-none border-2 ${errors.newPassword ? 'border-red-500' : 'border-gray-300'
                  }`}
                placeholder={t`Mật khẩu mới`}
                {...register('newPassword')}
              />
              <div
                onClick={() => setShowPass({ newPassword: !showPass.newPassword, retypePassword: showPass.retypePassword })}
                className="absolute top-3 right-3"
              >
                <svg
                  width="21"
                  height="21"
                  viewBox="0 0 21 21"
                  fill="none"
                  className={`w-6 h-6 ${showPass.newPassword && 'hidden'}`}
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="10.5" cy="10.5" r="3" stroke="#9EA3A9" />
                  <path
                    d="M17.5567 9.43786C17.9475 9.90312 18.1429 10.1357 18.1429 10.5C18.1429 10.8643 17.9475 11.0969 17.5567 11.5621C16.2682 13.0962 13.5922 15.75 10.5 15.75C7.40777 15.75 4.73181 13.0962 3.44331 11.5621C3.05251 11.0969 2.85711 10.8643 2.85711 10.5C2.85711 10.1357 3.05251 9.90312 3.4433 9.43786C4.73181 7.90384 7.40777 5.25 10.5 5.25C13.5922 5.25 16.2682 7.90384 17.5567 9.43786Z"
                    stroke="#9EA3A9"
                  />
                </svg>
                <svg
                  width="21"
                  height="21"
                  className={`w-6 h-6 ${!showPass.newPassword && 'hidden'}`}
                  viewBox="0 0 21 21"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M13.9684 10.972C13.9893 10.8177 14 10.6601 14 10.5C14 8.567 12.433 7 10.5 7C10.3399 7 10.1823 7.01075 10.028 7.03156L11.0592 8.06277C11.9909 8.27566 12.7243 9.0091 12.9372 9.94083L13.9684 10.972ZM8.22211 9.46835C8.07944 9.78287 8 10.1322 8 10.5C8 11.8807 9.11929 13 10.5 13C10.8678 13 11.2171 12.9206 11.5316 12.7779L12.2724 13.5187C11.7526 13.8246 11.1468 14 10.5 14C8.567 14 7 12.433 7 10.5C7 9.85322 7.17544 9.24741 7.48133 8.72757L8.22211 9.46835Z"
                    fill="#9EA3A9"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M14.0114 15.2574L13.2724 14.5184C12.4012 14.9645 11.4654 15.25 10.5003 15.25C9.09642 15.25 7.75468 14.6459 6.58311 13.8223C5.4149 13.0011 4.45533 11.9892 3.82647 11.2406C3.40001 10.7328 3.35742 10.6464 3.35742 10.5C3.35742 10.3536 3.40001 10.2672 3.82647 9.75945C4.38654 9.09266 5.20894 8.21713 6.20742 7.45344L5.49455 6.74057C4.46559 7.54414 3.62865 8.44017 3.06075 9.11628L3.00246 9.18546L3.00246 9.18546C2.66849 9.58123 2.35742 9.94985 2.35742 10.5C2.35742 11.0502 2.66849 11.4188 3.00246 11.8145L3.06075 11.8837C3.72041 12.6691 4.74307 13.7512 6.00802 14.6404C7.26961 15.5272 8.81197 16.25 10.5003 16.25C11.7728 16.25 12.9624 15.8394 14.0114 15.2574ZM8.18455 5.18793C8.91288 4.91658 9.68977 4.75 10.5003 4.75C12.1886 4.75 13.731 5.47277 14.9926 6.35963C16.2576 7.24884 17.2802 8.33093 17.9399 9.11628L17.9982 9.18546C18.3321 9.58122 18.6432 9.94985 18.6432 10.5C18.6432 11.0502 18.3321 11.4188 17.9982 11.8145L17.9399 11.8837C17.5574 12.339 17.053 12.8941 16.4501 13.4534L15.7424 12.7458C16.3179 12.2146 16.8035 11.6818 17.1741 11.2406C17.6006 10.7328 17.6432 10.6464 17.6432 10.5C17.6432 10.3536 17.6006 10.2672 17.1741 9.75945C16.5453 9.01077 15.5857 7.99892 14.4175 7.17772C13.2459 6.35414 11.9042 5.75 10.5003 5.75C9.98148 5.75 9.47114 5.83251 8.97474 5.97812L8.18455 5.18793Z"
                    fill="#9EA3A9"
                  />
                  <path d="M4.375 1.75L18.375 15.75" stroke="#9EA3A9" />
                </svg>
              </div>
            </div>
            <div
              className={`text-right text-red-500 font-bold mr-2 mt-1 text-xs w-96`}
            >
              {errors.newPassword?.message ? (
                i18n._(t`${errors.newPassword.message}`)
              ) : (
                <>&nbsp;</>
              )}
            </div>
          </div>
          <div className={'mx-10 py-1 lg:py-3'}>
            <div className={`font-bold mr-2 mt-1 text-luat_HN_primary`}>
              <Trans>Nhập lại mật khẩu mới</Trans>
            </div>
            <div className="relative w-full">
              <input
                type={showPass.retypePassword ? 'text' : 'password'}
                className={`w-full py-2 pl-5 pr-10 rounded-xl focus:ring outline-none border-2 ${errors.retypePassword ? 'border-red-500' : 'border-gray-300'
                  }`}
                placeholder={t`Nhập lại mật khẩu mới`}
                {...register('retypePassword')}
              />
              <div
                onClick={() => setShowPass({ newPassword: showPass.newPassword, retypePassword: !showPass.retypePassword })}
                className="absolute top-3 right-3"
              >
                <svg
                  width="21"
                  height="21"
                  viewBox="0 0 21 21"
                  fill="none"
                  className={`w-6 h-6 ${showPass.retypePassword && 'hidden'}`}
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="10.5" cy="10.5" r="3" stroke="#9EA3A9" />
                  <path
                    d="M17.5567 9.43786C17.9475 9.90312 18.1429 10.1357 18.1429 10.5C18.1429 10.8643 17.9475 11.0969 17.5567 11.5621C16.2682 13.0962 13.5922 15.75 10.5 15.75C7.40777 15.75 4.73181 13.0962 3.44331 11.5621C3.05251 11.0969 2.85711 10.8643 2.85711 10.5C2.85711 10.1357 3.05251 9.90312 3.4433 9.43786C4.73181 7.90384 7.40777 5.25 10.5 5.25C13.5922 5.25 16.2682 7.90384 17.5567 9.43786Z"
                    stroke="#9EA3A9"
                  />
                </svg>
                <svg
                  width="21"
                  height="21"
                  className={`w-6 h-6 ${!showPass.retypePassword && 'hidden'}`}
                  viewBox="0 0 21 21"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M13.9684 10.972C13.9893 10.8177 14 10.6601 14 10.5C14 8.567 12.433 7 10.5 7C10.3399 7 10.1823 7.01075 10.028 7.03156L11.0592 8.06277C11.9909 8.27566 12.7243 9.0091 12.9372 9.94083L13.9684 10.972ZM8.22211 9.46835C8.07944 9.78287 8 10.1322 8 10.5C8 11.8807 9.11929 13 10.5 13C10.8678 13 11.2171 12.9206 11.5316 12.7779L12.2724 13.5187C11.7526 13.8246 11.1468 14 10.5 14C8.567 14 7 12.433 7 10.5C7 9.85322 7.17544 9.24741 7.48133 8.72757L8.22211 9.46835Z"
                    fill="#9EA3A9"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M14.0114 15.2574L13.2724 14.5184C12.4012 14.9645 11.4654 15.25 10.5003 15.25C9.09642 15.25 7.75468 14.6459 6.58311 13.8223C5.4149 13.0011 4.45533 11.9892 3.82647 11.2406C3.40001 10.7328 3.35742 10.6464 3.35742 10.5C3.35742 10.3536 3.40001 10.2672 3.82647 9.75945C4.38654 9.09266 5.20894 8.21713 6.20742 7.45344L5.49455 6.74057C4.46559 7.54414 3.62865 8.44017 3.06075 9.11628L3.00246 9.18546L3.00246 9.18546C2.66849 9.58123 2.35742 9.94985 2.35742 10.5C2.35742 11.0502 2.66849 11.4188 3.00246 11.8145L3.06075 11.8837C3.72041 12.6691 4.74307 13.7512 6.00802 14.6404C7.26961 15.5272 8.81197 16.25 10.5003 16.25C11.7728 16.25 12.9624 15.8394 14.0114 15.2574ZM8.18455 5.18793C8.91288 4.91658 9.68977 4.75 10.5003 4.75C12.1886 4.75 13.731 5.47277 14.9926 6.35963C16.2576 7.24884 17.2802 8.33093 17.9399 9.11628L17.9982 9.18546C18.3321 9.58122 18.6432 9.94985 18.6432 10.5C18.6432 11.0502 18.3321 11.4188 17.9982 11.8145L17.9399 11.8837C17.5574 12.339 17.053 12.8941 16.4501 13.4534L15.7424 12.7458C16.3179 12.2146 16.8035 11.6818 17.1741 11.2406C17.6006 10.7328 17.6432 10.6464 17.6432 10.5C17.6432 10.3536 17.6006 10.2672 17.1741 9.75945C16.5453 9.01077 15.5857 7.99892 14.4175 7.17772C13.2459 6.35414 11.9042 5.75 10.5003 5.75C9.98148 5.75 9.47114 5.83251 8.97474 5.97812L8.18455 5.18793Z"
                    fill="#9EA3A9"
                  />
                  <path d="M4.375 1.75L18.375 15.75" stroke="#9EA3A9" />
                </svg>
              </div>
            </div>
            <div
              className={'text-right text-red-500 font-bold mr-2 mt-1 text-xs'}
            >
              {errors.retypePassword?.message ? (
                i18n._(t`${errors.retypePassword.message}`)
              ) : (
                <>&nbsp;</>
              )}
            </div>
          </div>
          <div className={'mx-auto py-5 md:py-2 lg:py-5'}>
            <button
              className={
                'mx-2 text-white bg-luat_HN_primary hover:bg-luat_HN_primary_hover py-2 w-36 rounded-lg'
              }
              type={'submit'}
            >
              <Trans>Xác nhận</Trans>
            </button>
          </div>
        </form>
      </div>
    </AuthLayout>
  );
};

export default ChangePassword;
